import React from "react";
import { Loader2, ShieldCheck, AlertTriangle, ArrowRight, BadgeDollarSign, CheckCircle2, Activity, Flame } from "lucide-react";
import { AlignedChip } from "@/components/AlignedAlerts";
import { PlayerName } from "@/components/PlayerCardDialog";
import { GreenBallHomeTile } from "@/components/GreenBallHomeTile";
import { FirstBucketHomeTile } from "@/components/FirstBucketHomeTile";

const Box = ({ title, icon: Icon, link, onGo, children, testId }) => (
  <section className="rounded-xl border border-slate-800 bg-[#0B0F17] p-4" data-testid={testId}>
    <div className="mb-3 flex items-center justify-between">
      <p className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wide text-slate-100"><Icon className="h-4 w-4 text-cyan-400" />{title}</p>
      {link && (
        <button onClick={onGo} data-testid={`${testId}-link`} className="flex min-h-[36px] items-center gap-1 rounded-lg px-2 font-mono text-[11px] font-bold uppercase text-cyan-300 transition-colors hover:bg-cyan-950/50">
          {link} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
    {children}
  </section>
);

const GoalieTag = ({ g }) => !g ? <span className="text-slate-600">TBD</span> : (
  <span className="whitespace-nowrap">
    <span className="text-slate-200">{g.name}</span>{" "}
    <span className={`rounded px-1 text-[9px] font-bold ${g.status === "CONFIRMED" ? "bg-emerald-950 text-emerald-300" : "bg-amber-950 text-amber-300"}`}>{g.status === "CONFIRMED" ? "CONF" : "PROJ"}</span>
  </span>
);

const Empty = ({ children }) => <p className="py-4 text-center font-mono text-[11px] text-slate-600">{children}</p>;

export default function TonightHome({ games, summary, onGo }) {
  const s = summary || {};
  if (s.loading) return <div className="flex flex-col items-center py-24 text-slate-500"><Loader2 className="h-7 w-7 animate-spin text-cyan-500" /><p className="mt-3 font-mono text-[11px] uppercase tracking-widest">Building tonight's summary…</p></div>;
  const approved = (s.card?.targets || []).filter((t) => t.status === "APPROVED").slice(0, 5);
  const value = (s.odds?.players || []).filter((o) => o.value).sort((a, b) => b.edge - a.edge).slice(0, 5);
  const allG = (s.goalies?.games || []).flatMap((g) => [g.away_goalie, g.home_goalie]).filter(Boolean);
  const alerts = [...(s.goalies?.conflicts || []).map((c) => ({ kind: "conflict", text: typeof c === "string" ? c : JSON.stringify(c) })),
    ...allG.filter((g) => g.status !== "CONFIRMED").map((g) => ({ kind: "proj", text: `${g.team}: ${g.name} not confirmed yet` }))];
  const lastNight = (s.audit?.nights || []).find((n) => n.graded);

  return (
    <div className="space-y-4" data-testid="tonight-home">
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="slate-strip">
        {(s.goalies?.games || []).map((g) => (
          <button key={g.game_key} onClick={() => onGo("targets", g.game_key)} data-testid={`slate-game-${g.game_key}`}
            className="min-w-[220px] shrink-0 rounded-xl border border-slate-800 bg-[#0B0F17] px-3 py-2.5 text-left transition-colors hover:border-cyan-700">
            <p className="font-display text-base font-bold text-white">{g.away_team} <span className="text-slate-600">@</span> {g.home_team}</p>
            <p className="mt-1 font-mono text-[10px] text-slate-400"><GoalieTag g={g.away_goalie} /></p>
            <p className="font-mono text-[10px] text-slate-400"><GoalieTag g={g.home_goalie} /></p>
          </button>
        ))}
        {!s.goalies?.games?.length && <Empty>{games?.length ? "Goalie data loading…" : "No games on this date"}</Empty>}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Box title="Top Official Card picks" icon={CheckCircle2} link="Official Card" onGo={() => onGo("official")} testId="home-card">
          {approved.length ? approved.map((t, i) => (
            <div key={`${t.team}-${t.jersey}`} className="flex items-center justify-between gap-2 border-b border-slate-800/60 py-2 last:border-0" data-testid={`home-pick-${i}`}>
              <div className="min-w-0">
                <PlayerName name={t.player} team={t.team} className="font-display text-sm font-semibold uppercase text-slate-100" />
                <p className="font-mono text-[10px] text-slate-500">{t.team} {t.slot} · vs {t.goalie} · {t.game}</p>
              </div>
              <span className="rounded bg-emerald-950 px-1.5 py-0.5 font-mono text-[11px] font-bold text-emerald-300">{t.score}</span>
            </div>
          )) : <Empty>No APPROVED picks yet</Empty>}
        </Box>
        <Box title="Top VALUE odds" icon={BadgeDollarSign} link="Game Lines" onGo={() => onGo("lines")} testId="home-value">
          {value.length ? value.map((o, i) => (
            <div key={`${o.team}-${o.name}`} className="flex items-center justify-between gap-2 border-b border-slate-800/60 py-2 last:border-0" data-testid={`home-value-${i}`}>
              <div className="min-w-0">
                <PlayerName name={o.name} team={o.team} className="font-display text-sm font-semibold uppercase text-slate-100" />
                <p className="font-mono text-[10px] text-slate-500">{o.team} vs {o.opp} · implied {o.implied}% · model {o.model}%</p>
              </div>
              <span className="text-right font-mono text-[11px]"><span className="font-bold text-emerald-300">{o.best}</span> <span className="text-slate-500">{o.best_book}</span><br /><span className="text-emerald-400">+{o.edge}</span></span>
            </div>
          )) : <Empty>No VALUE lines right now</Empty>}
        </Box>
        <Box title="Goalie alerts" icon={AlertTriangle} link="Goalie Origin" onGo={() => onGo("goalie")} testId="home-alerts">
          {alerts.length ? alerts.slice(0, 8).map((a, i) => (
            <p key={i} className={`py-1 font-mono text-[11px] ${a.kind === "conflict" ? "text-rose-300" : "text-amber-300"}`} data-testid={`home-alert-${i}`}>• {a.text}</p>
          )) : <p className="flex items-center gap-2 py-3 font-mono text-[11px] text-emerald-300"><ShieldCheck className="h-4 w-4" /> All starters confirmed, no conflicts</p>}
        </Box>
        <Box title="Last night's audit" icon={Activity} link="Audit" onGo={() => onGo("audit")} testId="home-audit">
          {lastNight ? (
            <div className="grid grid-cols-3 gap-2">
              {[["CYPHER top 5", lastNight.engines?.cypher_top5?.rate], ["APPROVED", lastNight.engines?.official_approved?.rate], ["Goalies", lastNight.goalie_accuracy?.rate]].map(([l, v]) => (
                <div key={l} className="rounded-lg border border-slate-800 bg-slate-950/60 px-2 py-2 text-center">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-slate-500">{l}</p>
                  <p className="font-display text-xl font-bold text-amber-300">{v != null ? `${v}%` : "—"}</p>
                </div>
              ))}
              <p className="col-span-3 font-mono text-[10px] text-slate-500">Graded {lastNight.date.slice(4, 6)}/{lastNight.date.slice(6, 8)}</p>
            </div>
          ) : <Empty>First audit results arrive the morning after tonight's games.</Empty>}
        </Box>
        <Box title="Aligned scorer alerts" icon={Flame} link="Numerology" onGo={() => onGo("numerology")} testId="home-aligned">
          {(s.aligned || []).length ? (
            <div className="flex flex-wrap gap-1.5">{s.aligned.map((r) => <AlignedChip key={r.player_id} r={r} />)}</div>
          ) : <Empty>No aligned-tonight skater has scored on an aligned night this season</Empty>}
          <p className="mt-2 font-mono text-[9px] text-slate-600">Aligned tonight + at least one goal on an aligned night this season · hit % = aligned nights scored / aligned nights played</p>
        </Box>
        <GreenBallHomeTile onGo={onGo} />
        <FirstBucketHomeTile onGo={onGo} />
      </div>
    </div>
  );
}
